import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Send } from "lucide-react";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { useToast } from "@/hooks/use-toast";
import { useSkills, useCurrentOrgId } from "@/hooks/useSkillOS";

interface LabResult {
  output?: string;
  trace_id?: string;
  task_class?: string;
  skill_matched?: string;
  pre_response_confidence?: number;
  metacog_decision?: string;
  verifier_verdict?: string;
  raw?: unknown;
  error?: string;
}

export default function SkillLab() {
  const { toast } = useToast();
  const { data: orgId } = useCurrentOrgId();
  const { data: skills = [] } = useSkills();
  const [message, setMessage] = useState("");
  const [skillName, setSkillName] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<Array<{ message: string; result: LabResult; ts: number }>>([]);

  const send = async () => {
    if (!message.trim() || !orgId) return;
    setLoading(true);
    try {
      // If a skill is pinned → use skill-executor in test mode (lab); else go through skill-analyzer for full pipeline preview
      let result: LabResult;
      if (skillName) {
        const { data, error } = await supabase.functions.invoke("skill-executor", {
          body: {
            skill_name: skillName,
            context: { message, org_id: orgId },
            org_id: orgId,
            test_mode: true,
            lab_mode: true,
          },
        });
        if (error) throw error;
        const d = data as Record<string, unknown>;
        result = {
          output: (d.output as string) ?? JSON.stringify(d),
          skill_matched: skillName,
          metacog_decision: (d.metacog_decision as string) ?? undefined,
          pre_response_confidence: (d.pre_response_confidence as number) ?? undefined,
          raw: d,
        };
      } else {
        const { data, error } = await supabase.functions.invoke("skill-analyzer", {
          body: { message, org_id: orgId, lab_mode: true },
        });
        if (error) throw error;
        const d = data as Record<string, unknown>;
        result = {
          output: (d.output as string) ?? (d.preview as string) ?? JSON.stringify(d),
          task_class: (d.task_class as string) ?? undefined,
          skill_matched: (d.skill_matched as string) ?? (d.skill as string) ?? undefined,
          pre_response_confidence: (d.pre_response_confidence as number) ?? undefined,
          metacog_decision: (d.metacog_decision as string) ?? undefined,
          verifier_verdict: (d.verifier_verdict as string) ?? undefined,
          trace_id: (d.trace_id as string) ?? undefined,
          raw: d,
        };
      }
      setHistory((h) => [{ message, result, ts: Date.now() }, ...h].slice(0, 20));
      setMessage("");
    } catch (e) {
      const err = e instanceof Error ? e.message : String(e);
      toast({ title: "Erro no lab", description: err, variant: "destructive" });
      setHistory((h) =>
        [{ message, result: { error: err }, ts: Date.now() }, ...h].slice(0, 20),
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Breadcrumb items={[{ label: "Skills", to: "/skills" }, { label: "Lab" }]} />
      <div>
        <h1 className="text-2xl font-bold font-headline">Skill Lab</h1>
        <p className="text-sm text-muted-foreground">
          Teste o pipeline R1..R7 sem enviar mensagens reais ao WhatsApp. Útil para depurar roteamento, metacog e verificador.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Entrada</CardTitle>
            <CardDescription>Selecione uma skill para forçar execução, ou deixe em branco para roteamento automático.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <select
              className="w-full bg-background border rounded-md h-9 px-2 text-sm"
              value={skillName}
              onChange={(e) => setSkillName(e.target.value)}
            >
              <option value="">— Roteamento automático —</option>
              {skills.map((s) => (
                <option key={s.id} value={s.name}>
                  {s.name} ({s.rollout_state})
                </option>
              ))}
            </select>
            <Textarea
              placeholder="Digite a mensagem como se fosse um usuário do WhatsApp..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
            />
            <Button onClick={send} disabled={loading || !message.trim() || !orgId} className="w-full">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              Enviar (lab)
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Trace</CardTitle>
            <CardDescription>{history.length} execuções nesta sessão</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-h-[600px] overflow-auto space-y-3">
              {history.length === 0 && (
                <div className="text-xs text-muted-foreground py-6 text-center">Sem execuções ainda.</div>
              )}
              {history.map((h, i) => (
                <div key={i} className="border rounded-md p-3 space-y-2">
                  <div className="text-xs text-muted-foreground">{new Date(h.ts).toLocaleTimeString()}</div>
                  <div className="text-sm whitespace-pre-wrap">{h.message}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {h.result.task_class && <Badge variant="outline">task: {h.result.task_class}</Badge>}
                    {h.result.skill_matched && <Badge>skill: {h.result.skill_matched}</Badge>}
                    {h.result.metacog_decision && (
                      <Badge variant="outline">metacog: {h.result.metacog_decision}</Badge>
                    )}
                    {h.result.pre_response_confidence != null && (
                      <Badge variant="outline">
                        conf: {Number(h.result.pre_response_confidence).toFixed(2)}
                      </Badge>
                    )}
                    {h.result.verifier_verdict && (
                      <Badge variant="outline">verifier: {h.result.verifier_verdict}</Badge>
                    )}
                  </div>
                  {h.result.error ? (
                    <div className="text-xs text-destructive">{h.result.error}</div>
                  ) : (
                    <pre className="p-2 bg-muted rounded text-xs whitespace-pre-wrap max-h-48 overflow-auto">
                      {h.result.output ?? "—"}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
